import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../i18nContext';
import StaticPageLayout from './StaticPageLayout';
import { Shield, Lock, Server, Eye, FileCheck, RefreshCw, UserCheck, AlertTriangle } from 'lucide-react';

interface Props { onBack: () => void; }

export default function SecurityPage({ onBack }: Props) {
  const { language } = useLanguage();
  const en = language === 'en';

  return (
    <StaticPageLayout title={en ? 'Security' : '安全合规'} onBack={onBack}>
      <Helmet>
        <title>{en ? 'Security & Compliance - MinutesAI' : '安全合规 - MinutesAI'}</title>
        <meta name="description" content={en ? 'MinutesAI security infrastructure: TLS 1.3 encryption, AES-256 at rest, 99.9% uptime SLA, role-based access control, and compliance standards.' : 'MinutesAI 安全基础设施：TLS 1.3 加密、AES-256 静态加密、99.9% 正常运行时间 SLA、基于角色的访问控制和合规标准。'} />
        <link rel="canonical" href="https://minutesai.com/security" />
        <meta property="og:title" content={en ? 'Security & Compliance - MinutesAI' : '安全合规 - MinutesAI'} />
        <meta property="og:url" content="https://minutesai.com/security" />
      </Helmet>

      {/* Hero */}
      <div className="text-center mb-16">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-900/10 mb-6">
          <Shield size={32} className="text-brand-900" />
        </div>
        <h2 className="text-4xl font-bold text-brand-900 font-serif mb-4">
          {en ? 'Security & Compliance' : '安全与合规'}
        </h2>
        <p className="text-lg text-ink-secondary max-w-2xl mx-auto font-normal leading-relaxed">
          {en
            ? 'Your meeting content is sensitive. We treat it that way — with enterprise-grade encryption, strict access controls, and a commitment to never selling your data.'
            : '您的会议内容至关重要。我们以企业级加密、严格的访问控制和绝不出售数据的承诺来保护它。'}
        </p>
      </div>

      {/* Core commitments */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
        <div className="bg-white rounded-2xl border border-surface-border p-8">
          <div className="text-brand-700 mb-4"><Lock size={24} /></div>
          <h3 className="text-xl font-bold text-brand-900 mb-3">{en ? 'Encryption Everywhere' : '全链路加密'}</h3>
          <p className="text-ink-secondary leading-relaxed">
            {en
              ? 'All data is encrypted in transit using TLS 1.3 and at rest using AES-256. Audio files are processed in isolated environments and never stored after transcription is complete.'
              : '所有数据在传输过程中使用 TLS 1.3 加密，静态存储使用 AES-256 加密。音频文件在隔离环境中处理，转录完成后不予保留。'}
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-surface-border p-8">
          <div className="text-brand-700 mb-4"><Server size={24} /></div>
          <h3 className="text-xl font-bold text-brand-900 mb-3">{en ? 'Reliable Infrastructure' : '可靠的基础设施'}</h3>
          <p className="text-ink-secondary leading-relaxed">
            {en
              ? 'Hosted on enterprise-grade cloud infrastructure with a 99.9% uptime SLA, automatic failover, and daily backups. Your data is always available when you need it.'
              : '托管于企业级云基础设施，提供 99.9% 正常运行时间 SLA、自动故障转移和每日备份，确保您随时可以访问数据。'}
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-surface-border p-8">
          <div className="text-brand-700 mb-4"><UserCheck size={24} /></div>
          <h3 className="text-xl font-bold text-brand-900 mb-3">{en ? 'Access Control' : '访问控制'}</h3>
          <p className="text-ink-secondary leading-relaxed">
            {en
              ? 'Role-based access control, session management, and anomalous login detection protect your account. Enterprise plans support SSO integration and organizational admin controls.'
              : '基于角色的访问控制、会话管理和异常登录检测保护您的账户。企业版支持 SSO 集成和组织级管理员控制。'}
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-surface-border p-8">
          <div className="text-brand-700 mb-4"><Eye size={24} /></div>
          <h3 className="text-xl font-bold text-brand-900 mb-3">{en ? 'Privacy by Default' : '默认隐私保护'}</h3>
          <p className="text-ink-secondary leading-relaxed">
            {en
              ? 'Meeting notes are private by default. You control what gets shared and with whom. We do not sell your personal data or meeting content to any third party.'
              : '会议纪要默认私密。您完全掌控分享对象和范围。我们不会将您的个人数据或会议内容出售给任何第三方。'}
          </p>
        </div>
      </div>

      {/* AI & Data practices */}
      <div className="bg-brand-900 rounded-2xl p-8 md:p-12 text-white mb-16">
        <h3 className="text-2xl font-bold font-serif mb-6">{en ? 'Our AI Data Commitment' : '我们的 AI 数据承诺'}</h3>
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-6 h-6 rounded-full bg-brand-glow/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <div className="w-2 h-2 rounded-full bg-brand-glow" />
            </div>
            <p className="text-brand-100 leading-relaxed">
              {en
                ? 'We do not allow our AI model providers to use your meeting content or personal data to train their models.'
                : '我们不允许 AI 模型提供商使用您的会议内容或个人数据来训练其模型。'}
            </p>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-6 h-6 rounded-full bg-brand-glow/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <div className="w-2 h-2 rounded-full bg-brand-glow" />
            </div>
            <p className="text-brand-100 leading-relaxed">
              {en
                ? 'MinutesAI may use de-identified, anonymized data to improve our own models. You can opt out at any time in account settings.'
                : 'MinutesAI 可能使用去标识化、匿名化的数据来改进我们自己的模型。您可以随时在账户设置中选择退出。'}
            </p>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-6 h-6 rounded-full bg-brand-glow/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <div className="w-2 h-2 rounded-full bg-brand-glow" />
            </div>
            <p className="text-brand-100 leading-relaxed">
              {en
                ? 'Enterprise customers have AI model training disabled by default. Your content stays yours.'
                : '企业客户默认禁用 AI 模型训练。您的内容始终属于您。'}
            </p>
          </div>
        </div>
      </div>

      {/* Security practices */}
      <div className="mb-16">
        <h3 className="text-2xl font-bold text-brand-900 font-serif mb-8 text-center">
          {en ? 'Security Practices' : '安全实践'}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl border border-surface-border p-6">
            <div className="text-brand-700 mb-3"><FileCheck size={20} /></div>
            <h4 className="font-bold text-brand-900 mb-2">{en ? 'Regular Audits' : '定期审计'}</h4>
            <p className="text-sm text-ink-secondary leading-relaxed">
              {en
                ? 'Regular security assessments and penetration testing to identify and remediate vulnerabilities before they can be exploited.'
                : '定期安全评估和渗透测试，在漏洞被利用前识别并修复。'}
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-surface-border p-6">
            <div className="text-brand-700 mb-3"><RefreshCw size={20} /></div>
            <h4 className="font-bold text-brand-900 mb-2">{en ? 'Continuous Monitoring' : '持续监控'}</h4>
            <p className="text-sm text-ink-secondary leading-relaxed">
              {en
                ? 'Automated vulnerability scanning, dependency monitoring, and real-time anomaly detection across all production systems.'
                : '对所有生产系统进行自动化漏洞扫描、依赖项监控和实时异常检测。'}
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-surface-border p-6">
            <div className="text-brand-700 mb-3"><AlertTriangle size={20} /></div>
            <h4 className="font-bold text-brand-900 mb-2">{en ? 'Incident Response' : '事件响应'}</h4>
            <p className="text-sm text-ink-secondary leading-relaxed">
              {en
                ? 'Documented incident response procedures with defined escalation paths. Affected users are notified promptly in the event of a security incident.'
                : '有文档记录的事件响应流程和明确的升级路径。发生安全事件时，及时通知受影响用户。'}
            </p>
          </div>
        </div>
      </div>

      {/* Data retention */}
      <div className="bg-white rounded-2xl border border-surface-border p-8 md:p-12 mb-16">
        <h3 className="text-2xl font-bold text-brand-900 font-serif mb-6">{en ? 'Data Retention & Deletion' : '数据保留与删除'}</h3>
        <div className="space-y-4 text-ink-secondary leading-relaxed">
          <p>{en ? 'Meeting notes and generated content are retained as long as your account is active. You can delete individual notes or your entire account at any time.' : '会议纪要和生成内容在您的账户有效期间保留。您可以随时删除单条纪要或整个账户。'}</p>
          <p>{en ? 'Upon account deletion, all personal data and content is permanently removed from our systems within 30 days.' : '账户删除后，所有个人数据和内容将在 30 天内从我们的系统中永久删除。'}</p>
          <p>{en ? 'Enterprise plans support custom data retention policies to meet your organization\'s compliance requirements.' : '企业版支持自定义数据保留策略，以满足您组织的合规要求。'}</p>
        </div>
      </div>

      {/* Contact */}
      <div className="text-center bg-surface rounded-2xl border border-surface-border p-10 flex flex-col items-center">
        <h3 className="text-xl font-bold text-brand-900 font-serif mb-3">
          {en ? 'Security questions or concerns?' : '有安全问题或疑虑？'}
        </h3>
        <p className="text-ink-secondary mb-4">
          {en ? 'Our security team is available to answer questions and review enterprise security requirements.' : '我们的安全团队随时解答问题并审查企业安全需求。'}
        </p>
        <a href="mailto:official@minutes-ai.com" className="inline-block bg-brand-900 text-white px-8 py-3 rounded-full font-bold hover:bg-brand-800 transition-colors">
          {en ? 'Contact Security Team' : '联系安全团队'}
        </a>
      </div>
    </StaticPageLayout>
  );
}
